import { EntityContactRows } from '@/components/ui/entity-contact-rows'
import { EntityContactStrip } from '@/components/ui/entity-contact-strip'
import {
  EmailContactLink,
  PhoneContactLink,
  SocialContactLink,
  WebsiteContactLink,
} from '@/components/ui/social-contact-link'
import { normalizeSocialText } from '@/lib/social-contact'
import {
  extraSocialFieldsFrom,
  resolveExtraSocialOrder,
  socialUrlFrom,
  type ExtraSocialFields,
} from '@/lib/social-networks'
import { cn } from '@/lib/utils'

export type EntityContactSize = 'sm' | 'base'

function contactSizeClass(size: EntityContactSize) {
  return size === 'sm' ? 'text-sm text-muted-foreground' : 'text-base text-muted-foreground'
}

export type PublicContactValue = {
  website?: string | null
  linkedin_url?: string | null
  x_url?: string | null
  extra_social_order?: string[] | null
} & Partial<ExtraSocialFields>

function hasPublicContactValue(contact: PublicContactValue) {
  const extras = extraSocialFieldsFrom(contact)
  return Boolean(
    contact.website?.trim() ||
      contact.linkedin_url?.trim() ||
      contact.x_url?.trim() ||
      resolveExtraSocialOrder(contact, extras).length,
  )
}

type PersonalContactStripProps = {
  email?: string | null
  phone?: string | null
  className?: string
  size?: EntityContactSize
}

/** Email and phone — private / direct contact row. */
export function PersonalContactStrip({
  email,
  phone,
  className,
  size = 'base',
}: PersonalContactStripProps) {
  const hasEmail = Boolean(email?.trim())
  const hasPhone = Boolean(phone?.trim())

  if (!hasEmail && !hasPhone) {
    return null
  }

  return (
    <EntityContactStrip className={cn(contactSizeClass(size), className)}>
      {hasEmail ? <EmailContactLink value={email} /> : null}
      {hasPhone ? <PhoneContactLink value={phone} /> : null}
    </EntityContactStrip>
  )
}

type PublicContactStripProps = PublicContactValue & {
  className?: string
  size?: EntityContactSize
  websitePresentation?: 'hostname' | 'visitSite'
}

/** Website and social links — public-facing contact row; hidden when all empty. */
export function PublicContactStrip({
  website,
  linkedin_url,
  x_url,
  className,
  size = 'base',
  websitePresentation = 'hostname',
  ...extras
}: PublicContactStripProps) {
  const contact = { website, linkedin_url, x_url, ...extras }
  if (!hasPublicContactValue(contact)) {
    return null
  }

  const extraValues = extraSocialFieldsFrom(contact)
  const websiteValue = normalizeSocialText(website)
  const socialNetworks = resolveExtraSocialOrder(contact, extraValues)

  return (
    <EntityContactStrip className={cn(contactSizeClass(size), className)}>
      {websiteValue ? (
        <WebsiteContactLink value={websiteValue} presentation={websitePresentation} />
      ) : null}
      {socialNetworks.map((network) => {
        const value = normalizeSocialText(socialUrlFrom(contact, network))
        return value ? <SocialContactLink key={network} network={network} value={value} /> : null
      })}
    </EntityContactStrip>
  )
}

type PersonContactSectionProps = {
  email?: string | null
  phone?: string | null
  className?: string
  size?: EntityContactSize
} & PublicContactValue

/** Person contact block — personal row (email/phone) then public links row. */
export function PersonContactSection({
  email,
  phone,
  website,
  linkedin_url,
  x_url,
  className,
  size = 'base',
  ...extras
}: PersonContactSectionProps) {
  return (
    <EntityContactRows className={className}>
      <PersonalContactStrip
        email={email}
        phone={phone}
        size={size}
      />
      <PublicContactStrip
        website={website}
        linkedin_url={linkedin_url}
        x_url={x_url}
        size={size}
        {...extras}
      />
    </EntityContactRows>
  )
}
